import { useRef, useCallback, useState } from 'react';
import { useResizeObserver, useEventListener } from '@react-hookz/web';
import { T } from 'gt-react';
import { Camera } from '@phosphor-icons/react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AgentIcon } from '@/components/common/AgentIcon';
import { ChatMessage } from './ChatMessage';
import { ChatMarkdown } from './chat-markdown';
import { CHAT_FONT_FAMILY } from './chat-styles';
import { cn } from '@/lib/utils';
import { getFriendlyToolName } from '@/lib/workflows/toolFriendlyNames';
import type {
  Message,
  MessageToolResult,
  ToolCall,
} from '@/context/agent-state.context';
import type { ReactNode } from 'react';

interface ChatMessageListProps {
  messages: Message[];
  streamingText?: string;
  isLoading: boolean;
  isCapturingScreen?: boolean;
  error?: string | null;
  activeToolCalls?: ToolCall[];
  className?: string;
  messageMaxWidth?: string;
  /** Optional renderer for tool results attached to assistant messages */
  renderToolResult?: (
    message: Message,
    toolResult: MessageToolResult,
  ) => ReactNode;
  excludeToolResultIds?: ReadonlySet<string>;
  onFocusNodes?: (nodeIds: string[]) => void;
}

/**
 * Progress kind reported by every workflow draft mutation tool. Kept in
 * sync with `DraftProgressKind` in the backend tools file. The frontend
 * groups consecutive same-kind entries into a single collapsible bucket
 * so the chat log stays compact even on multi-step builds.
 */
type ProgressKind =
  | 'set_metadata'
  | 'add_step'
  | 'update_config'
  | 'remove_step'
  | 'add_edge'
  | 'remove_edge'
  | 'set_static_input'
  | 'clear_static_input'
  | 'reset';

interface Progress {
  kind: ProgressKind;
  label: string;
}

const KIND_INFO: Record<
  ProgressKind,
  { verb: string; noun: string; nounPlural: string }
> = {
  set_metadata: { verb: 'Updated', noun: 'metadata', nounPlural: 'metadata' },
  add_step: { verb: 'Added', noun: 'step', nounPlural: 'steps' },
  update_config: { verb: 'Configured', noun: 'step', nounPlural: 'steps' },
  remove_step: { verb: 'Removed', noun: 'step', nounPlural: 'steps' },
  add_edge: { verb: 'Connected', noun: 'edge', nounPlural: 'edges' },
  remove_edge: { verb: 'Disconnected', noun: 'edge', nounPlural: 'edges' },
  set_static_input: { verb: 'Pinned', noun: 'input', nounPlural: 'inputs' },
  clear_static_input: { verb: 'Unpinned', noun: 'input', nounPlural: 'inputs' },
  reset: { verb: 'Reset', noun: 'draft', nounPlural: 'drafts' },
};

function extractProgress(result: unknown): Progress | null {
  if (!result || typeof result !== 'object') return null;
  const progress = (result as { progress?: unknown }).progress;
  if (!progress || typeof progress !== 'object') return null;
  const { kind, label } = progress as { kind?: unknown; label?: unknown };
  if (typeof kind !== 'string' || typeof label !== 'string') return null;
  if (!(kind in KIND_INFO)) return null;
  return { kind: kind as ProgressKind, label };
}

function extractNodeIds(tc: ToolCall): string[] {
  const ids: string[] = [];
  const args = tc.args as Record<string, unknown>;

  switch (tc.toolName) {
    case 'draft_add_step': {
      if (tc.result && typeof tc.result === 'object') {
        const result = tc.result as Record<string, unknown>;
        const progress = result.progress as Record<string, unknown> | undefined;
        if (progress && typeof progress.stepId === 'string') {
          ids.push(progress.stepId);
        } else if (typeof result.stepId === 'string') {
          ids.push(result.stepId);
        }
      }
      break;
    }
    case 'draft_remove_step':
    case 'draft_update_step_config': {
      if (typeof args.stepId === 'string') {
        ids.push(args.stepId);
      }
      break;
    }
    case 'draft_add_edge':
    case 'draft_remove_edge': {
      if (typeof args.sourceStepId === 'string') {
        ids.push(args.sourceStepId);
      }
      if (typeof args.targetStepId === 'string') {
        ids.push(args.targetStepId);
      }
      break;
    }
  }

  return ids;
}

interface ProgressBucket {
  /** Bucket id is the first toolCallId — stable across renders */
  id: string;
  kind: ProgressKind;
  /** Labels of the completed entries in this bucket, in order */
  labels: string[];
  /** Pending entries in this bucket (most recent last) */
  pending: ToolCall[];
  nodeIds: string[];
}

function bucketizeToolCalls(toolCalls: readonly ToolCall[]): {
  buckets: ProgressBucket[];
  /** Pending tool calls that don't carry a progress kind (e.g. inspect_step_type) */
  unkeyedPending: ToolCall[];
} {
  const buckets: ProgressBucket[] = [];
  // Same-kind ops nest into one bucket regardless of interleaving with
  // other kinds. The bucket is positioned where its FIRST entry landed
  // so the user still gets a sense of when each phase started.
  const bucketByKind = new Map<ProgressKind, ProgressBucket>();
  const unkeyedPending: ToolCall[] = [];

  for (const tc of toolCalls) {
    if (tc.status === 'pending') {
      // Pending calls have no result yet. We can't know their kind until
      // the result arrives, so keep them in a side bin rendered as a
      // generic "Working on …" line. Once the result lands, the same
      // toolCallId will be classified into a bucket on the next render.
      unkeyedPending.push(tc);
      continue;
    }
    const progress = extractProgress(tc.result);
    if (!progress) continue;

    const nodeIds = extractNodeIds(tc);
    const existing = bucketByKind.get(progress.kind);
    if (existing) {
      existing.labels.push(progress.label);
      existing.nodeIds.push(...nodeIds);
    } else {
      const bucket: ProgressBucket = {
        id: tc.toolCallId,
        kind: progress.kind,
        labels: [progress.label],
        pending: [],
        nodeIds: [...nodeIds],
      };
      bucketByKind.set(progress.kind, bucket);
      buckets.push(bucket);
    }
  }

  return { buckets, unkeyedPending };
}

function bucketSummary(bucket: ProgressBucket): string {
  const info = KIND_INFO[bucket.kind];
  if (bucket.kind === 'set_metadata') {
    // metadata is always 1 — collapse into a verbatim line
    return 'Updated workflow metadata';
  }
  if (bucket.labels.length === 1) {
    // Single-item buckets render the label inline so the user doesn't
    // have to expand to see what changed
    if (
      bucket.kind === 'add_edge' ||
      bucket.kind === 'remove_edge' ||
      bucket.kind === 'set_static_input' ||
      bucket.kind === 'clear_static_input'
    ) {
      return `${info.verb} ${bucket.labels[0]}`;
    }
    return `${info.verb} "${bucket.labels[0]}"`;
  }
  return `${info.verb} ${bucket.labels.length} ${info.nounPlural}`;
}

function ProgressLog({
  toolCalls,
  onFocusNodes,
}: {
  toolCalls: readonly ToolCall[];
  onFocusNodes?: (nodeIds: string[]) => void;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { buckets, unkeyedPending } = bucketizeToolCalls(toolCalls);

  if (buckets.length === 0 && unkeyedPending.length === 0) return null;

  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="space-y-1">
      {buckets.map((bucket) => {
        const isExpanded = expanded[bucket.id] ?? false;
        const expandable = bucket.labels.length > 1;
        const isClickable =
          bucket.nodeIds.length > 0 && onFocusNodes !== undefined;
        return (
          <div key={bucket.id} className="text-sm text-stone-500">
            <button
              type="button"
              className={cn(
                'flex items-center gap-2 text-left w-full',
                expandable && 'hover:text-stone-700',
                isClickable &&
                  'cursor-pointer hover:text-foreground hover:underline',
              )}
              onClick={
                isClickable
                  ? () => onFocusNodes([...new Set(bucket.nodeIds)])
                  : expandable
                    ? () => toggle(bucket.id)
                    : undefined
              }
              disabled={!expandable && !isClickable}
            >
              <span className="inline-block w-1 h-1 bg-stone-400 rounded-full shrink-0" />
              <span>{bucketSummary(bucket)}</span>
              {expandable && (
                <span
                  role="button"
                  tabIndex={0}
                  className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(bucket.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      toggle(bucket.id);
                    }
                  }}
                >
                  {/* eslint-disable-next-line i18next/no-literal-string -- UI glyph, not translatable */}
                  {isExpanded ? '▾' : '▸'}
                </span>
              )}
            </button>
            {expandable && isExpanded && (
              <ul className="ml-6 mt-0.5 space-y-0.5 text-xs text-stone-500">
                {bucket.labels.map((label, i) => (
                  <li key={i}>
                    {'·'} {label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {unkeyedPending.map((tc) => (
        <div
          key={tc.toolCallId}
          className="flex items-center gap-2 text-sm text-stone-500 animate-pulse"
        >
          <span className="inline-block w-1 h-1 bg-stone-400 rounded-full" />
          {tc.userFacingName ?? getFriendlyToolName(tc.toolName)}
        </div>
      ))}
    </div>
  );
}

export function ChatMessageList({
  messages,
  streamingText,
  isLoading,
  isCapturingScreen = false,
  error,
  activeToolCalls = [],
  className,
  messageMaxWidth = 'max-w-[85%]',
  renderToolResult,
  excludeToolResultIds,
  onFocusNodes,
}: ChatMessageListProps) {
  const isWaitingForFirstToken =
    isLoading &&
    !isCapturingScreen &&
    !streamingText &&
    messages.at(-1)?.role === 'user';

  // `error` counts as content: the error block lives in the main return, so
  // without this an error-only state would fall through to the empty-state
  // placeholder and the error would never render.
  const hasContent =
    messages.length > 0 ||
    isLoading ||
    Boolean(streamingText) ||
    Boolean(error);

  // Autoscroll, stated as two declarative DOM subscriptions (react-hookz) rather
  // than hand-rolled effects:
  //   • useResizeObserver — fires on ANY height change to the message container
  //     (new message, streamed token, tool-progress line, late-loading card),
  //     which a prop-dependency effect alone misses (it never sees async heights
  //     or activeToolCalls). This is the "follow the newest content" trigger.
  //   • useEventListener('scroll') — records the user's intent so we can follow
  //     live WITHOUT yanking them back when they scroll up to read history.
  const contentRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<HTMLElement | null>(null);
  // Default true: a fresh chat opens pinned to the newest message.
  const stickToBottom = useRef(true);

  // The real scroll container is the Radix viewport wrapping our content. Resolve
  // it from the content node via Radix's stable data attribute — no ref
  // forwarding through the ScrollArea wrapper, no document querySelector. Stored
  // in state only so useEventListener gets a concrete target to bind to.
  const setContentNode = useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    setViewport(
      node?.closest<HTMLElement>('[data-radix-scroll-area-viewport]') ?? null,
    );
  }, []);

  const isNearBottom = (el: HTMLElement): boolean =>
    el.scrollHeight - el.scrollTop - el.clientHeight < 80;

  // Capture whether the user is parked at the bottom on every scroll — BEFORE
  // the next content growth — so the resize handler knows whether re-pinning is
  // welcome or would fight a deliberate scroll-up.
  useEventListener(viewport, 'scroll', () => {
    if (viewport) stickToBottom.current = isNearBottom(viewport);
  });

  // Re-pin to the newest content on any height change, but only while the user
  // is following along at the bottom. Setting scrollTop directly (vs
  // scrollIntoView) keeps the scroll contained to the viewport and never nudges
  // ancestor scrollers or the page.
  useResizeObserver(contentRef, () => {
    if (viewport && stickToBottom.current) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  });

  if (!hasContent) {
    return (
      <ScrollArea
        className={cn('flex-1 min-h-0 px-3', className)}
        style={{ fontFamily: CHAT_FONT_FAMILY }}
      >
        <div className="flex items-center justify-center h-full py-8">
          <p className="text-sm text-muted-foreground">
            <T context="chat message list empty state prompt">
              Start a conversation with the AI assistant.
            </T>
          </p>
        </div>
      </ScrollArea>
    );
  }

  // Anchor the progress log to the user message that started the
  // current turn so it stays in chronological order when the assistant
  // response lands. Otherwise the log gets pushed below the assistant
  // text + proposal card and reads as if it happened after.
  const lastUserIdx = messages.findLastIndex((m) => m.role === 'user');
  const renderMessage = (msg: Message) => (
    <div key={msg.id}>
      <ChatMessage
        role={msg.role}
        content={msg.content}
        maxWidth={messageMaxWidth}
        hasScreenshot={msg.hasScreenshot}
        screenshotDataUrl={msg.screenshotDataUrl}
        dbMessageId={msg.dbMessageId}
        traceId={msg.traceId}
        observationId={msg.observationId}
        attachments={msg.attachments}
        badge={msg.badge}
      />
      {renderToolResult &&
        msg.role === 'assistant' &&
        msg.toolResults
          ?.filter((tr) => !excludeToolResultIds?.has(tr.toolCallId))
          .map((tr) => (
            <div key={tr.toolCallId} className="mt-2 ml-0">
              {renderToolResult(msg, tr)}
            </div>
          ))}
    </div>
  );

  return (
    <ScrollArea
      className={cn('flex-1 min-h-0 px-3', className)}
      style={{ fontFamily: CHAT_FONT_FAMILY }}
    >
      <div ref={setContentNode} className="space-y-4 py-4">
        {messages.slice(0, lastUserIdx + 1).map(renderMessage)}

        {isCapturingScreen && messages.at(-1)?.role === 'user' && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 p-2.5 text-sm text-stone-500">
              <Camera className="h-4 w-4 animate-pulse" weight="fill" />
              <span>
                <T context="chat capturing screen indicator">
                  Capturing screen...
                </T>
              </span>
            </div>
          </div>
        )}

        <ProgressLog toolCalls={activeToolCalls} onFocusNodes={onFocusNodes} />

        {messages.slice(lastUserIdx + 1).map(renderMessage)}

        {isWaitingForFirstToken && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 p-2.5">
              <AgentIcon state="loading" size={24} />
            </div>
          </div>
        )}

        {isLoading && streamingText && (
          <div className="flex justify-start">
            <div className={cn(messageMaxWidth, 'text-foreground')}>
              <ChatMarkdown>{streamingText}</ChatMarkdown>
            </div>
          </div>
        )}

        {error && (
          <div className="flex justify-start">
            <div
              className={cn(
                messageMaxWidth,
                'rounded-2xl bg-red-50 px-3.5 py-2.5 text-red-800',
              )}
            >
              <p className="text-sm font-medium">
                <T context="chat error message heading">Error</T>
              </p>
              <p className="text-sm leading-relaxed">{error}</p>
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
